import readAndSignDocPdfIcon from '../../assets/images/honeycomb-icons/read-and-sign.svg'
import surveyIcon from '../../assets/images/honeycomb-icons/survey.svg'
import articleIcon from '../../assets/images/honeycomb-icons/article.svg'
import audioFileIcon from '../../assets/images/honeycomb-icons/audio-file.svg'
import frontalCourseIcon from '../../assets/images/honeycomb-icons/frontal-course.svg'
import videoFileIcon from '../../assets/images/honeycomb-icons/video.svg'
import canvaIcon from '../../assets/images/honeycomb-icons/canva.svg'
import coursewareIcon from '../../assets/images/honeycomb-icons/courseware.svg'
import examIcon from '../../assets/images/honeycomb-icons/exam.svg'
import learningPathIcon from '../../assets/images/honeycomb-icons/learning-path.svg'
import PodcastIcon from '@mui/icons-material/Podcasts';
import videoCourseIcon from '../../assets/images/honeycomb-icons/online-course.svg'
import linkIcon from '../../assets/images/honeycomb-icons/link.svg'
import uploadDocumentIcon from '../../assets/images/honeycomb-icons/article.svg'
import {filter, get, map, some} from 'lodash'


export const SignDocTypes = {
    READ_AND_SIGN_DOC_PDF: 'READ_AND_SIGN_DOC_PDF',
    WATCH_AND_SIGN_YOUTUBE_LINK: 'WATCH_AND_SIGN_YOUTUBE_LINK',
    WATCH_AND_SIGN_VIMEO_LINK: 'WATCH_AND_SIGN_VIMEO_LINK',
    WATCH_AND_SIGN_VIDEO_FILE: 'WATCH_AND_SIGN_VIDEO_FILE',
    REGULAR_COURSE: 'REGULAR_COURSE',
    CANVA_PRESENTATION: 'CANVA_PRESENTATION',
    EXAM: 'EXAM',
    LEARNING_PATH: 'LEARNING_PATH',
    SURVEY: 'SURVEY',
    ARTICLE: 'ARTICLE',
    AUDIO_FILE: 'AUDIO_FILE',
    LINK: 'LINK',
    PODCAST: 'PODCAST',
    FRONTAL_COURSE: 'FRONTAL_COURSE',
    ONLINE_COURSE: 'ONLINE_COURSE',
    PRESENTATION: 'PRESENTATION',
    WEEZBEE_COURSE: 'WEEZBEE_COURSE',
    UPLOAD_DOCUMENT: 'UPLOAD_DOCUMENT',
}

export const SignDocUploadStatus = {
    UPLOADING: 'UPLOADING',
    FAILED: 'FAILED',
    READY: 'READY',
}

/**
 * Pagination Configuration for SignDoc Assignment Modal
 * Defines page sizes for user and organization selection in CreateEditNewSignDocModal
 */
export const SIGNDOC_ASSIGNMENT_PAGINATION = {
    USERS_PAGE_SIZE: 30,        // Number of users to display per page in individual users tab
    ORGANIZATIONS_PAGE_SIZE: 30  // Number of organizations to display per page in organizations tab
}

/**
 * Duration Unit Types
 * Defines the valid time units for duration-based assignments and expiry periods
 * These values must match the backend constants in SignDocLib.js
 */
export const DurationUnits = {
    MINUTES: 'MINUTES',
    DAYS: 'DAYS',
    MONTHS: 'MONTHS',
    YEARS: 'YEARS'
};

/**
 * Valid duration units for expiry and finishBy durations (excludes MINUTES)
 * Used for validation and filtering
 */
export const VALID_DURATION_UNITS = [DurationUnits.DAYS, DurationUnits.MONTHS, DurationUnits.YEARS];

/**
 * Default values for each duration unit
 * Used when user selects a unit without entering a value
 */
export const DEFAULT_DURATION_VALUES = {
    [DurationUnits.MINUTES]: 5,
    [DurationUnits.DAYS]: 60,
    [DurationUnits.MONTHS]: 2,
    [DurationUnits.YEARS]: 1
};

/**
 * Maximum allowed values for each duration unit
 * Enforces reasonable limits for duration inputs
 */
export const MAX_DURATION_VALUES = {
    [DurationUnits.MINUTES]: 60,
    [DurationUnits.DAYS]: 365,
    [DurationUnits.MONTHS]: 36,
    [DurationUnits.YEARS]: 3
};

/**
 * Recurring Duration Units
 * Configurable via REACT_APP_SIGNDOC_RECURRING_DURATION_UNITS env var (pipe-separated, e.g. "MINUTES|MONTHS|YEARS")
 * Defaults to [MONTHS, YEARS] if not set
 */
const DEFAULT_RECURRING_UNITS = [DurationUnits.MONTHS, DurationUnits.YEARS];
const parseRecurringDurationUnits = () => {
    // Use default if env var not set
    const envValue = process.env.REACT_APP_SIGNDOC_RECURRING_DURATION_UNITS;
    if (!envValue) return DEFAULT_RECURRING_UNITS;

    // Split pipe-separated values (e.g. "MINUTES|MONTHS|YEARS")
    const validValues = Object.values(DurationUnits);
    const parts = envValue.split('|');

    // All values must be exact DurationUnits matches (case-sensitive); any invalid value → full fallback
    const allValid = parts.length > 0 && parts.every(u => validValues.includes(u));
    if (!allValid) {
        console.warn(`Invalid REACT_APP_SIGNDOC_RECURRING_DURATION_UNITS: "${envValue}". Falling back to [MONTHS, YEARS].`);
        return DEFAULT_RECURRING_UNITS;
    }

    return parts;
};
export const RECURRING_DURATION_UNITS = parseRecurringDurationUnits();

/**
 * Whether past dates are allowed for recurringAt (nextVersionDueDate).
 * Set REACT_APP_RECURRING_SIGNDOC_ALLOW_PAST_DATE=true in .env.local for staging/local to test recurring cron jobs immediately.
 * @type {boolean}
 */
export const RECURRING_SIGNDOC_ALLOW_PAST_DATE = process.env.REACT_APP_RECURRING_SIGNDOC_ALLOW_PAST_DATE === 'true';

/**
 * Default values for recurring duration
 */
export const DEFAULT_RECURRING_DURATION = {
    value: 12,
    unit: DurationUnits.MONTHS
};

/**
 * Minimum allowed values for recurring duration
 * Prevents users from setting unreasonably short recurring intervals
 */
export const MIN_RECURRING_DURATION_VALUES = {
    [DurationUnits.MINUTES]: 1,
    [DurationUnits.DAYS]: 1,
    [DurationUnits.MONTHS]: 1,
    [DurationUnits.YEARS]: 1
};

/**
 * Maximum allowed values for recurring duration
 * Limits how far in the future recurring can be scheduled
 */
export const MAX_RECURRING_DURATION_VALUES = {
    [DurationUnits.MINUTES]: 60,
    [DurationUnits.DAYS]: 365,
    [DurationUnits.MONTHS]: 36,
    [DurationUnits.YEARS]: 5
};

/**
 * Default duration in days for SignDoc completion deadlines.
 * Must match backend constant in SignDocLib.js
 */
export const DEFAULT_DURATION_DAYS = 60;

/**
 * Sentinel value for "no deadline" - user has unlimited time to complete.
 * Must match backend constant NO_DURATION in SignDocLib.js
 */
export const NO_DURATION = -1;

/**
 * Course types that use sessionStartDate instead of finishByDuration.
 * These types have scheduled sessions rather than duration-based deadlines.
 */
export const SESSION_BASED_COURSE_TYPES = [
    SignDocTypes.FRONTAL_COURSE,
    SignDocTypes.ONLINE_COURSE,
];

export const SIGNDOC_STATUS = {
    ACTIVE: 'ACTIVE',
    ARCHIVED: 'ARCHIVED',
    SCHEDULED: 'SCHEDULED'  // Reserved for future use
};

export const USER_SIGNDOC_STATUS = {
    ACTIVE: 'ACTIVE',
    ARCHIVED: 'ARCHIVED',
};

export const SIGNDOC_GROUP_TYPES = {
    SEQUENTIAL: 'SEQUENTIAL',
    PARALLEL: 'PARALLEL'
};

/**
 * Default group type for new SignDocs
 */
export const DEFAULT_GROUP_TYPE = SIGNDOC_GROUP_TYPES.PARALLEL;

/**
 * Default status for new SignDocs
 */
export const DEFAULT_SIGNDOC_STATUS = SIGNDOC_STATUS.ACTIVE;

/**
 * Video type consolidation constants for filter UI
 * These allow displaying a single "VIDEOS" option instead of 3 separate video types
 */
export const VIDEO_TYPE = 'VIDEOS';
export const VIDEO_TYPES = [
    SignDocTypes.WATCH_AND_SIGN_YOUTUBE_LINK,
    SignDocTypes.WATCH_AND_SIGN_VIMEO_LINK,
    SignDocTypes.WATCH_AND_SIGN_VIDEO_FILE
];

export const signDocTypeConfig = {
    [SignDocTypes.READ_AND_SIGN_DOC_PDF]: {
        learningManagementSubPath: 'signDocs',
        mgmntImageIcon: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/signDocPDF.png`,
        signDocOptionsTypes: [SignDocTypes.READ_AND_SIGN_DOC_PDF],
        getSignDocLinkPath: ({id}) => `/signDoc/${id}`,
        isPreviewable: true,
        previewMode: 'signedUrl',
        reportsBasePath: '/reports/course-results',
        createEditNewSignDocModal: {
            createButtonTextKey: 'newDocSign.modal.createButtonText.READ_AND_SIGN_DOC_PDF',

            requiresFileUpload: true,
            requiresLink: false,
            docNameLabelKey: 'newDocSign.modal.docNameLabel.READ_AND_SIGN_DOC_PDF',
            docDescriptionLabelKey: 'newDocSign.modal.docDescriptionLabel.READ_AND_SIGN_DOC_PDF',
            dropDocLabelKey: 'newDocSign.modal.dropDocLabel.READ_AND_SIGN_DOC_PDF',
            extraSaveButtonTextKey: null,
            fileUploadConfig: {
                maxSize: 50 * 1024 * 1024, // 50MB for PDF files
                accept: {
                    'application/pdf': ['.pdf']
                },
                supportedFileTypesForNotifications: ['PDF']
            }
        },
        honeyCombModal: {
            honeyCombImageIcon: readAndSignDocPdfIcon,
            isHoneyCombSecondaryImageIcon: false,
            isAIImplemented: false,
            honeyCombTextKey: 'HoneycombTasks.servicesText.READ_AND_SIGN_DOC_PDF',
            columnPosition: 0,
            rowPosition: 0,
        },
        mySignDocs: {
            firstTitleKey: 'learningCenter.pdfDocsToDo',
            secondTitleKey: 'learningCenter.pdfDocsCompleted',
            emptyStateTextsTodo: 'learningCenter.noPdfDocsToDo',
            emptyStateTextsCompleted: 'learningCenter.noPdfDocsCompleted'
        },
    },
    [SignDocTypes.WATCH_AND_SIGN_YOUTUBE_LINK]: {
        learningManagementSubPath: 'videos',
        mgmntImageIcon: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/SignDocVideo.png`,
        signDocOptionsTypes: [SignDocTypes.WATCH_AND_SIGN_YOUTUBE_LINK],
        getSignDocLinkPath: ({id}) => `/signDoc/${id}`,
        isPreviewable: true,
        previewMode: 'externalLink',
        reportsBasePath: '/reports/course-results',
        createEditNewSignDocModal: {
            createButtonTextKey: 'newDocSign.modal.createButtonText.WATCH_AND_SIGN_YOUTUBE_LINK',

            requiresFileUpload: false,
            requiresLink: true,
            docNameLabelKey: 'newDocSign.modal.docNameLabel.WATCH_AND_SIGN_YOUTUBE_LINK',
            docDescriptionLabelKey: 'newDocSign.modal.docDescriptionLabel.WATCH_AND_SIGN_YOUTUBE_LINK',
            extraSaveButtonTextKey: null,
            shouldShowAllowFastForwardCheckbox: true
        },
        honeyCombModal: {
            honeyCombImageIcon: videoFileIcon,
            isHoneyCombSecondaryImageIcon: false,
            isAIImplemented: false,
            honeyCombTextKey: 'HoneycombTasks.servicesText.WATCH_AND_SIGN_YOUTUBE_LINK',
            columnPosition: undefined,
            rowPosition: undefined,
        },
        mySignDocs: {
            firstTitleKey: 'learningCenter.videoLinksToDo',
            secondTitleKey: 'learningCenter.videoLinksCompleted',
            emptyStateTextsTodo: 'learningCenter.noVideoLinksToDo',
            emptyStateTextsCompleted: 'learningCenter.noVideoLinksCompleted'
        },
    },
    [SignDocTypes.WATCH_AND_SIGN_VIMEO_LINK]: {
        learningManagementSubPath: 'videos',
        mgmntImageIcon: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/SignDocVideo.png`,
        signDocOptionsTypes: [SignDocTypes.WATCH_AND_SIGN_VIMEO_LINK],
        getSignDocLinkPath: ({id}) => `/signDoc/${id}`,
        isPreviewable: true,
        previewMode: 'externalLink',
        reportsBasePath: '/reports/course-results',
        createEditNewSignDocModal: {
            createButtonTextKey: 'newDocSign.modal.createButtonText.WATCH_AND_SIGN_VIMEO_LINK',

            requiresFileUpload: false,
            requiresLink: true,
            docNameLabelKey: 'newDocSign.modal.docNameLabel.WATCH_AND_SIGN_VIMEO_LINK',
            docDescriptionLabelKey: 'newDocSign.modal.docDescriptionLabel.WATCH_AND_SIGN_VIMEO_LINK',
            extraSaveButtonTextKey: null,
            shouldShowAllowFastForwardCheckbox: true
        },
        honeyCombModal: {
            honeyCombImageIcon: videoFileIcon,
            isHoneyCombSecondaryImageIcon: false,
            isAIImplemented: false,
            honeyCombTextKey: 'HoneycombTasks.servicesText.WATCH_AND_SIGN_VIMEO_LINK',
            columnPosition: undefined,
            rowPosition: undefined,
        },
        mySignDocs: {
            firstTitleKey: 'learningCenter.videoLinksToDo',
            secondTitleKey: 'learningCenter.videoLinksCompleted',
            emptyStateTextsTodo: 'learningCenter.noVideoLinksToDo',
            emptyStateTextsCompleted: 'learningCenter.noVideoLinksCompleted'
        },
    },
    [SignDocTypes.WATCH_AND_SIGN_VIDEO_FILE]: {
        learningManagementSubPath: 'videos',
        mgmntImageIcon: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/SignDocVideo.png`,
        signDocOptionsTypes: [SignDocTypes.WATCH_AND_SIGN_VIDEO_FILE, SignDocTypes.WATCH_AND_SIGN_VIMEO_LINK, SignDocTypes.WATCH_AND_SIGN_YOUTUBE_LINK],
        getSignDocLinkPath: ({id}) => `/signDoc/${id}`,
        isPreviewable: true,
        previewMode: 'signedUrl',
        reportsBasePath: '/reports/course-results',
        createEditNewSignDocModal: {
            createButtonTextKey: 'newDocSign.modal.createButtonText.WATCH_AND_SIGN_VIDEO_FILE',

            requiresFileUpload: true,
            requiresLink: false,
            docNameLabelKey: 'newDocSign.modal.docNameLabel.WATCH_AND_SIGN_VIDEO_FILE',
            docDescriptionLabelKey: 'newDocSign.modal.docDescriptionLabel.WATCH_AND_SIGN_VIDEO_FILE',
            dropDocLabelKey: 'newDocSign.modal.dropDocLabel.WATCH_AND_SIGN_VIDEO_FILE',
            extraSaveButtonTextKey: null,
            shouldShowAllowFastForwardCheckbox: true,
            fileUploadConfig: {
                maxSize: 500 * 1024 * 1024, // 500MB for video files
                accept: {
                    'video/mp4': ['.mp4'],
                    'video/avi': ['.avi'],
                    'video/quicktime': ['.mov'],
                    'video/x-msvideo': ['.avi'],
                    'video/webm': ['.webm'],
                    'video/x-ms-wmv': ['.wmv']
                },
                supportedFileTypesForNotifications: ['MP4', 'AVI', 'MOV', 'WEBM', 'WMV']
            }
        },
        honeyCombModal: {
            honeyCombImageIcon: videoFileIcon,
            isHoneyCombSecondaryImageIcon: false,
            isAIImplemented: false,
            honeyCombTextKey: 'HoneycombTasks.servicesText.WATCH_AND_SIGN_VIDEO_FILE',
            columnPosition: 1,
            rowPosition: -1,
        },
        mySignDocs: {
            firstTitleKey: 'learningCenter.videoFilesToDo',
            secondTitleKey: 'learningCenter.videoFilesCompleted',
            emptyStateTextsTodo: 'learningCenter.noVideoFilesToDo',
            emptyStateTextsCompleted: 'learningCenter.noVideoFilesCompleted'
        },
    },
    [SignDocTypes.AUDIO_FILE]: {
        learningManagementSubPath: 'audio',
        mgmntImageIcon: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/AudioTask.png`,
        signDocOptionsTypes: [SignDocTypes.AUDIO_FILE],
        getSignDocLinkPath: ({id}) => `/signDoc/${id}`,
        isPreviewable: true,
        previewMode: 'signedUrl',
        reportsBasePath: '/reports/course-results',
        createEditNewSignDocModal: {
            createButtonTextKey: 'newDocSign.modal.createButtonText.AUDIO_FILE',

            requiresFileUpload: true,
            requiresLink: false,
            docNameLabelKey: 'newDocSign.modal.docNameLabel.AUDIO_FILE',
            docDescriptionLabelKey: 'newDocSign.modal.docDescriptionLabel.AUDIO_FILE',
            dropDocLabelKey: 'newDocSign.modal.dropDocLabel.AUDIO_FILE',
            extraSaveButtonTextKey: null,
            shouldShowAllowFastForwardCheckbox: true,
            fileUploadConfig: {
                maxSize: 50 * 1024 * 1024, // 50MB for audio files
                accept: {
                    'audio/mpeg': ['.mp3'],
                    'audio/wav': ['.wav'],
                    'audio/x-m4a': ['.m4a'],
                    'audio/aac': ['.aac'],
                    'audio/ogg': ['.ogg'],
                    'audio/flac': ['.flac']
                },
                supportedFileTypesForNotifications: ['MP3', 'WAV', 'M4A', 'AAC', 'OGG', 'FLAC']
            }
        },
        honeyCombModal: {
            honeyCombImageIcon: audioFileIcon,
            isHoneyCombSecondaryImageIcon: false,
            isAIImplemented: false,
            honeyCombTextKey: 'HoneycombTasks.servicesText.AUDIO_FILE',
            columnPosition: 2,
            rowPosition: -1,
        },
        mySignDocs: {
            firstTitleKey: 'learningCenter.audioFilesToDo',
            secondTitleKey: 'learningCenter.audioFilesCompleted',
            emptyStateTextsTodo: 'learningCenter.noAudioFilesToDo',
            emptyStateTextsCompleted: 'learningCenter.noAudioFilesCompleted'
        },
    },
    [SignDocTypes.REGULAR_COURSE]: {
        shouldRedirectOnCreation: true,
        learningManagementSubPath: 'courses',
        mgmntImageIcon: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/signDocPDF.png`,
        signDocOptionsTypes: [SignDocTypes.REGULAR_COURSE],
        getSignDocLinkPath: ({id}) => `/course/${id}`,
        isPreviewable: true,
        previewMode: 'externalLink',
        reportsBasePath: '/reports/course-results',
        createEditNewSignDocModal: {
            createButtonTextKey: 'newDocSign.modal.createButtonText.REGULAR_COURSE',

            requiresFileUpload: false,
            requiresLink: false,
            docNameLabelKey: 'newDocSign.modal.docNameLabel.REGULAR_COURSE',
            docDescriptionLabelKey: 'newDocSign.modal.docDescriptionLabel.REGULAR_COURSE',
            extraSaveButtonTextKey: null
        },
        honeyCombModal: {
            honeyCombImageIcon: coursewareIcon,
            isHoneyCombSecondaryImageIcon: false,
            isAIImplemented: false,
            honeyCombTextKey: 'HoneycombTasks.servicesText.REGULAR_COURSE',
            columnPosition: undefined, // Not included in honeycomb grid
            rowPosition: undefined,
        },
        mySignDocs: {
            firstTitleKey: 'learningCenter.regularCoursesToDo',
            secondTitleKey: 'learningCenter.regularCoursesCompleted',
            emptyStateTextsTodo: 'learningCenter.noRegularCoursesToDo',
            emptyStateTextsCompleted: 'learningCenter.noRegularCoursesCompleted'
        },
    },
    [SignDocTypes.CANVA_PRESENTATION]: {
        shouldRedirectOnCreation: true,
        learningManagementSubPath: 'presentations',
        getSignDocLinkPath: ({id}) => `/signDoc/${id}`,
        isPreviewable: true,
        previewMode: 'externalLink',
        mgmntImageIcon: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/SignDocCanva.png`,
        signDocOptionsTypes: [SignDocTypes.CANVA_PRESENTATION],
        reportsBasePath: '/reports/course-results',
        createEditNewSignDocModal: {
            createButtonTextKey: 'newDocSign.modal.createButtonText.CANVA_PRESENTATION',

            requiresFileUpload: false,
            requiresLink: true,
            docNameLabelKey: 'newDocSign.modal.docNameLabel.CANVA_PRESENTATION',
            docDescriptionLabelKey: 'newDocSign.modal.docDescriptionLabel.CANVA_PRESENTATION',
            extraSaveButtonTextKey: null
        },
        honeyCombModal: {
            honeyCombImageIcon: canvaIcon,
            isHoneyCombSecondaryImageIcon: false,
            isAIImplemented: false,
            honeyCombTextKey: 'HoneycombTasks.servicesText.CANVA_PRESENTATION',
            columnPosition: -1,
            rowPosition: 0,
        },
        mySignDocs: {
            firstTitleKey: 'learningCenter.canvaPresentationsToDo',
            secondTitleKey: 'learningCenter.canvaPresentationsCompleted',
            emptyStateTextsTodo: 'learningCenter.noCanvaPresentationsToDo',
            emptyStateTextsCompleted: 'learningCenter.noCanvaPresentationsCompleted'
        },
    },
    [SignDocTypes.EXAM]: {
        shouldRedirectOnCreation: true,
        learningManagementSubPath: 'exams',
        mgmntImageIcon: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/Exam.png`,
        signDocOptionsTypes: [SignDocTypes.EXAM],
        getSignDocLinkPath: ({id}) => `/questionnaire/${id}`,
        isPreviewable: false,
        reportsBasePath: '/reports/course-results',
        createEditNewSignDocModal: {
            createButtonTextKey: 'newDocSign.modal.createButtonText.EXAM',
            extraSaveButtonTextKey: 'newDocSign.modal.examExtraSaveButton',

            requiresFileUpload: false,
            requiresLink: false,
            redirectUrlOnCreate: (signDocId) => `/learningManagement/exams/${signDocId}/edit`,
            redirectUrlOnEdit: (signDocId) => `/learningManagement/exams/${signDocId}/edit`,
            docNameLabelKey: 'newDocSign.modal.docNameLabel.EXAM',
            docDescriptionLabelKey: 'newDocSign.modal.docDescriptionLabel.EXAM'
        },
        honeyCombModal: {
            honeyCombImageIcon: examIcon,
            isHoneyCombSecondaryImageIcon: true,
            isAIImplemented: true,
            honeyCombTextKey: 'HoneycombTasks.servicesText.EXAM',
            columnPosition: -2,
            rowPosition: 0,
        },
        mySignDocs: {
            firstTitleKey: 'learningCenter.examsToDo',
            secondTitleKey: 'learningCenter.examsCompleted',
            emptyStateTextsTodo: 'learningCenter.noExamsToDo',
            emptyStateTextsCompleted: 'learningCenter.noExamsCompleted'
        },
        questionnaireSession: {
            finishModalTitleKey: 'questionnaireSession.modal.finishExamTitle',
            finishModalTextKey: 'questionnaireSession.modal.finishExam',
            finishModalTextWithUnansweredKey: 'questionnaireSession.modal.finishExamWithUnansweredQuestions',
            closeButtonTextKey: 'questionnaireSession.modal.closeAndReturnToTest',
            nextButtonTextKey: 'questionnaireSession.buttons.nextQuestion',
            finishButtonTextKey: 'questionnaireSession.buttons.finishExam',
            failureMessageKey: 'questionnaireSession.finishExamFailure',
            successButtonTextKey: 'questionnaireSession.buttons.congratsModal',
            congratsTitleKey: 'questionnaireSession.finishModal.congratsTitle',
            congratsMessageKey: 'questionnaireSession.finishModal.congratsMessage',
            instructionModalComponent: 'QuestionnaireInstructionModal'
        },
        aiGeneration: {
            titleKey: 'AIGeneration.titles.EXAM',
            buttonTextKey: 'AIGeneration.buttonText.EXAM'
        },
    },
    [SignDocTypes.LEARNING_PATH]: {
        shouldRedirectOnCreation: true,
        learningManagementSubPath: 'learningPath',
        getSignDocLinkPath: ({id}) => `/learningPathViewer/${id}`,
        isPreviewable: false,
        mgmntImageIcon: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/LearningPath.png`,
        signDocOptionsTypes: [SignDocTypes.LEARNING_PATH],
        reportsBasePath: '/reports/course-results',
        createEditNewSignDocModal: {
            createButtonTextKey: 'newDocSign.modal.createButtonText.LEARNING_PATH',
            extraSaveButtonTextKey: 'newDocSign.modal.learningPathExtraSaveButton',

            requiresFileUpload: false,
            requiresLink: false,
            redirectUrlOnCreate: (signDocId) => `/learningManagement/learningPath/${signDocId}/edit`,
            redirectUrlOnEdit: (signDocId) => `/learningManagement/learningPath/${signDocId}/edit`,
            docNameLabelKey: 'newDocSign.modal.docNameLabel.LEARNING_PATH',
            docDescriptionLabelKey: 'newDocSign.modal.docDescriptionLabel.LEARNING_PATH'
        },
        honeyCombModal: {
            honeyCombImageIcon: learningPathIcon,
            isHoneyCombSecondaryImageIcon: false,
            isAIImplemented: false,
            honeyCombTextKey: 'HoneycombTasks.servicesText.LEARNING_PATH',
            columnPosition: -1,
            rowPosition: 1,
        },
        mySignDocs: {
            firstTitleKey: 'learningCenter.learningPathsToDo',
            secondTitleKey: 'learningCenter.learningPathsCompleted',
            emptyStateTextsTodo: 'learningCenter.noLearningPathsToDo',
            emptyStateTextsCompleted: 'learningCenter.noLearningPathsCompleted'
        },
    },
    [SignDocTypes.PODCAST]: {
        shouldRedirectOnCreation: false,
        learningManagementSubPath: null,
        isPreviewable: false,
        mgmntImageIcon: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/signDocPDF.png`,
        signDocOptionsTypes: [SignDocTypes.PODCAST],
        createEditNewSignDocModal: {
            createButtonTextKey: 'newDocSign.modal.createButtonText.PODCAST',

            requiresFileUpload: false,
            requiresLink: false,
            docNameLabelKey: 'newDocSign.modal.docNameLabel.PODCAST',
            docDescriptionLabelKey: 'newDocSign.modal.docDescriptionLabel.PODCAST',
            extraSaveButtonTextKey: null
        },
        honeyCombModal: {
            honeyCombImageIcon: <PodcastIcon />,
            isHoneyCombSecondaryImageIcon: true,
            isAIImplemented: false,
            honeyCombTextKey: 'HoneycombTasks.servicesText.PODCAST',
            columnPosition: undefined,
            rowPosition: undefined,
        },
        mySignDocs: {
            firstTitleKey: 'learningCenter.podcastsToDo',
            secondTitleKey: 'learningCenter.podcastsCompleted',
            emptyStateTextsTodo: 'learningCenter.noPodcastsToDo',
            emptyStateTextsCompleted: 'learningCenter.noPodcastsCompleted'
        },
    },
    [SignDocTypes.ONLINE_COURSE]: {
        shouldRedirectOnCreation: false,
        learningManagementSubPath: 'onlineCourses',
        mgmntImageIcon: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/OnlineCourse.png`,
        signDocOptionsTypes: [SignDocTypes.ONLINE_COURSE],
        getSignDocLinkPath: ({id}) => `/signDoc/${id}`,
        isPreviewable: false,
        reportsBasePath: '/reports/course-results',
        createEditNewSignDocModal: {
            createButtonTextKey: 'newDocSign.modal.createButtonText.ONLINE_COURSE',

            requiresFileUpload: false,
            requiresLink: false,
            docNameLabelKey: 'newDocSign.modal.docNameLabel.ONLINE_COURSE',
            docDescriptionLabelKey: 'newDocSign.modal.docDescriptionLabel.ONLINE_COURSE',
            extraSaveButtonTextKey: null
        },
        honeyCombModal: {
            honeyCombImageIcon: videoCourseIcon,
            isHoneyCombSecondaryImageIcon: false,
            isAIImplemented: false,
            honeyCombTextKey: 'HoneycombTasks.servicesText.ONLINE_COURSE',
            columnPosition: -2,
            rowPosition: 2,
        },
        mySignDocs: {
            firstTitleKey: 'learningCenter.onlineCoursesToDo',
            secondTitleKey: 'learningCenter.onlineCoursesCompleted',
            emptyStateTextsTodo: 'learningCenter.noOnlineCoursesToDo',
            emptyStateTextsCompleted: 'learningCenter.noOnlineCoursesCompleted'
        },
    },
    [SignDocTypes.PRESENTATION]: {
        shouldRedirectOnCreation: false,
        learningManagementSubPath: null,
        isPreviewable: false,
        mgmntImageIcon: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/signDocPDF.png`,
        signDocOptionsTypes: [SignDocTypes.PRESENTATION],
        createEditNewSignDocModal: {
            createButtonTextKey: 'newDocSign.modal.createButtonText.PRESENTATION',

            requiresFileUpload: false,
            requiresLink: false,
            docNameLabelKey: 'newDocSign.modal.docNameLabel.PRESENTATION',
            docDescriptionLabelKey: 'newDocSign.modal.docDescriptionLabel.PRESENTATION',
            extraSaveButtonTextKey: null
        },
        honeyCombModal: {
            honeyCombImageIcon: coursewareIcon,
            isHoneyCombSecondaryImageIcon: true,
            isAIImplemented: false,
            honeyCombTextKey: 'HoneycombTasks.servicesText.PRESENTATION',
            columnPosition: 0,
            rowPosition: -1,
        },
        mySignDocs: {
            firstTitleKey: 'learningCenter.presentationsToDo',
            secondTitleKey: 'learningCenter.presentationsCompleted',
            emptyStateTextsTodo: 'learningCenter.noPresentationsToDo',
            emptyStateTextsCompleted: 'learningCenter.noPresentationsCompleted'
        },
    },
    [SignDocTypes.WEEZBEE_COURSE]: {
        shouldRedirectOnCreation: true,
        learningManagementSubPath: 'weezBeeCourses',
        mgmntImageIcon: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/signDocPDF.png`,
        getSignDocLinkPath: ({id}) => `/weezbeeCourse/${id}`,
        isPreviewable: false,
        reportsBasePath: '/reports/course-results',
        createEditNewSignDocModal: {
            createButtonTextKey: 'newDocSign.modal.createButtonText.PRESENTATION',

            requiresFileUpload: false,
            requiresLink: false,
            docNameLabelKey: 'newDocSign.modal.docNameLabel.PRESENTATION',
            docDescriptionLabelKey: 'newDocSign.modal.docDescriptionLabel.PRESENTATION',
            redirectUrlOnCreate: (signDocId) => `/learningManagement/weezBeeCourse/${signDocId}/edit`,
            redirectUrlOnEdit: (signDocId) => `/learningManagement/weezBeeCourse/${signDocId}/edit`,
            extraSaveButtonTextKey: 'newDocSign.modal.weezBeeCourseExtraSaveButton',
            fileUploadConfig: {
                maxSize: 10 * 1024 * 1024, // 10MB for AI input files (PDF and TXT)
                accept: {
                    'application/pdf': ['.pdf'],
                    'text/plain': ['.txt']
                },
                supportedFileTypesForNotifications: ['PDF', 'TXT']
            }
        },
        honeyCombModal: {
            honeyCombImageIcon: coursewareIcon,
            isHoneyCombSecondaryImageIcon: true,
            isAIImplemented: true,
            honeyCombTextKey: 'HoneycombTasks.servicesText.WEEZBEE_COURSE',
            columnPosition: 1,
            rowPosition: 0,
        },
        mySignDocs: {
            firstTitleKey: 'learningCenter.presentationsToDo',
            secondTitleKey: 'learningCenter.presentationsCompleted',
            emptyStateTextsTodo: 'learningCenter.noPresentationsToDo',
            emptyStateTextsCompleted: 'learningCenter.noPresentationsCompleted'
        },
        aiGeneration: {
            titleKey: 'AIGeneration.titles.WEEZBEE_COURSE'
        },
    },
    [SignDocTypes.ARTICLE]: {
        shouldRedirectOnCreation: false,
        learningManagementSubPath: null,
        isPreviewable: false,
        mgmntImageIcon: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/signDocPDF.png`,
        signDocOptionsTypes: [SignDocTypes.ARTICLE],
        createEditNewSignDocModal: {
            createButtonTextKey: 'newDocSign.modal.createButtonText.ARTICLE',

            requiresFileUpload: false,
            requiresLink: false,
            docNameLabelKey: 'newDocSign.modal.docNameLabel.ARTICLE',
            docDescriptionLabelKey: 'newDocSign.modal.docDescriptionLabel.ARTICLE',
            extraSaveButtonTextKey: null
        },
        honeyCombModal: {
            honeyCombImageIcon: articleIcon,
            isHoneyCombSecondaryImageIcon: true,
            isAIImplemented: false,
            honeyCombTextKey: 'HoneycombTasks.servicesText.ARTICLE',
            columnPosition: undefined,
            rowPosition: undefined,
        },
        mySignDocs: {
            firstTitleKey: 'learningCenter.articlesToDo',
            secondTitleKey: 'learningCenter.articlesCompleted',
            emptyStateTextsTodo: 'learningCenter.noArticlesToDo',
            emptyStateTextsCompleted: 'learningCenter.noArticlesCompleted'
        },
    },
    [SignDocTypes.SURVEY]: {
        shouldRedirectOnCreation: true,
        learningManagementSubPath: 'surveys',
        mgmntImageIcon: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/Survey.png`,
        signDocOptionsTypes: [SignDocTypes.SURVEY],
        getSignDocLinkPath: ({id}) => `/questionnaire/${id}`,
        isPreviewable: false,
        reportsBasePath: '/reports/course-results',
        createEditNewSignDocModal: {
            createButtonTextKey: 'newDocSign.modal.createButtonText.SURVEY',
            extraSaveButtonTextKey: 'newDocSign.modal.surveyExtraSaveButton',

            requiresFileUpload: false,
            requiresLink: false,
            redirectUrlOnCreate: (signDocId) => `/learningManagement/surveys/${signDocId}/edit`,
            redirectUrlOnEdit: (signDocId) => `/learningManagement/surveys/${signDocId}/edit`,
            docNameLabelKey: 'newDocSign.modal.docNameLabel.SURVEY',
            docDescriptionLabelKey: 'newDocSign.modal.docDescriptionLabel.SURVEY'
        },
        honeyCombModal: {
            honeyCombImageIcon: surveyIcon,
            isHoneyCombSecondaryImageIcon: true,
            isAIImplemented: true,
            honeyCombTextKey: 'HoneycombTasks.servicesText.SURVEY',
            columnPosition: 0,
            rowPosition: 1,
        },
        mySignDocs: {
            firstTitleKey: 'learningCenter.surveysToDo',
            secondTitleKey: 'learningCenter.surveysCompleted',
            emptyStateTextsTodo: 'learningCenter.noSurveysToDo',
            emptyStateTextsCompleted: 'learningCenter.noSurveysCompleted'
        },
        questionnaireSession: {
            finishModalTitleKey: 'surveySession.modal.finishSurveyTitle',
            finishModalTextKey: 'surveySession.modal.finishSurvey',
            closeButtonTextKey: 'surveySession.modal.closeAndReturnToSurvey',
            nextButtonTextKey: 'surveySession.buttons.nextQuestion',
            finishButtonTextKey: 'surveySession.buttons.finishSurvey',
            successButtonTextKey: 'surveySession.buttons.congratsModal',
            congratsTitleKey: 'surveySession.finishModal.congratsTitle',
            congratsMessageKey: 'surveySession.finishModal.congratsMessage',
            instructionModalComponent: 'SurveyInstructionModal',
        },
        questionnaireResults: {
            hasQuestionAnalytics: true,
            resultsTabLabelKey: 'surveyResults.tabs.results',
            usersTabLabelKey: 'surveyResults.tabs.users'
        },
        aiGeneration: {
            titleKey: 'AIGeneration.titles.SURVEY',
            buttonTextKey: 'AIGeneration.buttonText.SURVEY'
        },
    },
    [SignDocTypes.LINK]: {
        shouldRedirectOnCreation: false,
        learningManagementSubPath: 'links',
        mgmntImageIcon: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/LinkTask.png`,
        getSignDocLinkPath: ({id}) => `/signDoc/${id}`,
        isPreviewable: true,
        previewMode: 'externalLink',
        reportsBasePath: '/reports/course-results',
        createEditNewSignDocModal: {
            createButtonTextKey: 'newDocSign.modal.createButtonText.LINK',

            requiresFileUpload: false,
            requiresLink: true,
            docNameLabelKey: 'newDocSign.modal.docNameLabel.LINK',
            docDescriptionLabelKey: 'newDocSign.modal.docDescriptionLabel.LINK',
            extraSaveButtonTextKey: null
        },
        honeyCombModal: {
            honeyCombImageIcon: linkIcon,
            isHoneyCombSecondaryImageIcon: false,
            isAIImplemented: false,
            honeyCombTextKey: 'HoneycombTasks.servicesText.LINK',
            columnPosition: -2,
            rowPosition: 1,
        },
        mySignDocs: {
            firstTitleKey: 'learningCenter.linksToDo',
            secondTitleKey: 'learningCenter.linksCompleted',
            emptyStateTextsTodo: 'learningCenter.noLinksToDo',
            emptyStateTextsCompleted: 'learningCenter.noLinksCompleted'
        },
    },
    [SignDocTypes.UPLOAD_DOCUMENT]: {
        learningManagementSubPath: 'uploads',
        mgmntImageIcon: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/signDocPDF.png`,
        signDocOptionsTypes: [SignDocTypes.UPLOAD_DOCUMENT],
        getSignDocLinkPath: ({id}) => `/signDoc/${id}`,
        isPreviewable: false,
        reportsBasePath: '/reports/course-results',
        createEditNewSignDocModal: {
            createButtonTextKey: 'newDocSign.modal.createButtonText.UPLOAD_DOCUMENT',

            requiresFileUpload: false, // Admins don't upload files - users upload their own documents
            requiresLink: false,
            docNameLabelKey: 'newDocSign.modal.docNameLabel.UPLOAD_DOCUMENT',
            docDescriptionLabelKey: 'newDocSign.modal.docDescriptionLabel.UPLOAD_DOCUMENT',
            dropDocLabelKey: 'newDocSign.modal.dropDocLabel.UPLOAD_DOCUMENT',
            extraSaveButtonTextKey: null,
            fileUploadConfig: {
                maxSize: 10 * 1024 * 1024, // 10MB for uploaded documents
                accept: {
                    'application/pdf': ['.pdf'],
                    'image/jpeg': ['.jpg', '.jpeg'],
                    'image/png': ['.png'],
                    'text/plain': ['.txt']
                },
                supportedFileTypesForNotifications: ['PDF', 'TXT', 'JPG', 'PNG']
            }
        },
        honeyCombModal: {
            honeyCombImageIcon: uploadDocumentIcon,
            isHoneyCombSecondaryImageIcon: false,
            isAIImplemented: false,
            honeyCombTextKey: 'HoneycombTasks.servicesText.UPLOAD_DOCUMENT',
            columnPosition: 2,
            rowPosition:-2,
        },
        mySignDocs: {
            firstTitleKey: 'learningCenter.uploadsToDo',
            secondTitleKey: 'learningCenter.uploadsCompleted',
            emptyStateTextsTodo: 'learningCenter.noUploadsToDo',
            emptyStateTextsCompleted: 'learningCenter.noUploadsCompleted'
        },
    },
    [SignDocTypes.FRONTAL_COURSE]: {
        shouldRedirectOnCreation: false,
        learningManagementSubPath: 'frontalCourses',
        mgmntImageIcon: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/FrontalCourse.png`,
        signDocOptionsTypes: [SignDocTypes.FRONTAL_COURSE],
        getSignDocLinkPath: ({id}) => `/signDoc/${id}`,
        isPreviewable: false,
        reportsBasePath: '/reports/course-results',
        createEditNewSignDocModal: {
            createButtonTextKey: 'newDocSign.modal.createButtonText.FRONTAL_COURSE',

            requiresFileUpload: false,
            requiresLink: false,
            docNameLabelKey: 'newDocSign.modal.docNameLabel.FRONTAL_COURSE',
            docDescriptionLabelKey: 'newDocSign.modal.docDescriptionLabel.FRONTAL_COURSE',
            extraSaveButtonTextKey: null
        },
        honeyCombModal: {
            honeyCombImageIcon: frontalCourseIcon,
            isHoneyCombSecondaryImageIcon: false,
            isAIImplemented: false,
            honeyCombTextKey: 'HoneycombTasks.servicesText.FRONTAL_COURSE',
            columnPosition: 2,
            rowPosition: 0,
        },
        mySignDocs: {
            firstTitleKey: 'learningCenter.frontalCoursesToDo',
            secondTitleKey: 'learningCenter.frontalCoursesCompleted',
            emptyStateTextsTodo: 'learningCenter.noFrontalCoursesToDo',
            emptyStateTextsCompleted: 'learningCenter.noFrontalCoursesCompleted'
        },
    }
};

export const getAllSignDocTypes = () => {
    return Object.values(SignDocTypes)
}

export const signDocTypeToFeatureFlagMap = {
    READ_AND_SIGN_DOC_PDF: 'signDocsReadAndSignDocPdfFF',
    WATCH_AND_SIGN_YOUTUBE_LINK: 'signDocsWatchAndSignYoutubeLinkFF',
    WATCH_AND_SIGN_VIMEO_LINK: 'signDocsWatchAndSignVimeoLinkFF',
    WATCH_AND_SIGN_VIDEO_FILE: 'signDocsWatchAndSignVideoFileFF',
    AUDIO_FILE: 'signDocsAudioFileFF',
    REGULAR_COURSE: 'regularCourseFF',
    CANVA_PRESENTATION: 'signDocCanvaPresentationFF',
    EXAM: 'examsFF',
    LEARNING_PATH: 'learningPathFF',
    PRESENTATION: 'presentationFF',
    SURVEY: 'surveyFF',
    ARTICLE: 'articleFF',
    LINK: 'linkFF',
    PODCAST: 'podcastFF',
    FRONTAL_COURSE: 'frontalCourseFF',
    ONLINE_COURSE: 'onlineCourseFF',
    WEEZBEE_COURSE: 'weezbeeCourseFF',
    UPLOAD_DOCUMENT: 'uploadDocumentFF'
}


export const signDocReadOrWatchMap = {
    READ_AND_SIGN_DOC_PDF: 'read',
    WATCH_AND_SIGN_YOUTUBE_LINK: 'watch',
    WATCH_AND_SIGN_VIMEO_LINK: 'watch',
    WATCH_AND_SIGN_VIDEO_FILE: 'watch',
    AUDIO_FILE: 'listen',
    CANVA_PRESENTATION: 'canva',
    LINK: 'link',
    SURVEY: 'survey',
    WEEZBEE_COURSE: 'read',
    UPLOAD_DOCUMENT: 'upload'
}

export const SignDocIcons = {
    READ_AND_SIGN_DOC_PDF: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/signDocPDF.png`,
    WATCH_AND_SIGN_YOUTUBE_LINK: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/SignDocVideo.png`,
    WATCH_AND_SIGN_VIMEO_LINK: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/SignDocVideo.png`,
    WATCH_AND_SIGN_VIDEO_FILE: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/SignDocVideo.png`,
    AUDIO_FILE: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/AudioTask.png`,
    CANVA_PRESENTATION: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/SignDocCanva.png`,
    EXAM: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/Exam.png`,
    LEARNING_PATH: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/LearningPath.png`,
    LINK: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/LinkTask.png`,
    WEEZBEE_COURSE: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/signDocPDF.png`,
    UPLOAD_DOCUMENT: `${process.env.REACT_APP_PUBLIC_FILES_URL}/images/signDocImages/signDocPDF.png`,
}

export const isYoutubeLink = ({linkUrl}) => {
    return linkUrl && (linkUrl.includes('youtube.com/watch') || linkUrl.includes('youtube.com/embed') || linkUrl.includes('youtu.be'))
}
export const isVimeoLink = ({linkUrl}) => {
    return linkUrl && linkUrl.includes('vimeo.com')
}
export const isCanvaLink = ({linkUrl}) => {
    return linkUrl && linkUrl.includes('canva.com')
}

export const getSignDocPrefix = ({signDoc, i18n}) => {
    if (signDoc.type === SignDocTypes.REGULAR_COURSE) {
        return i18n.translate('settingsPage.reminders.course')
    } else if (signDoc.type === SignDocTypes.READ_AND_SIGN_DOC_PDF) {
        return i18n.translate('settingsPage.reminders.signDoc')
    } else if (signDoc.type === SignDocTypes.WATCH_AND_SIGN_VIMEO_LINK || signDoc.type === SignDocTypes.WATCH_AND_SIGN_YOUTUBE_LINK || signDoc.type === SignDocTypes.WATCH_AND_SIGN_VIDEO_FILE) {
        return i18n.translate('settingsPage.reminders.watchVideo')
    } else if (signDoc.type === SignDocTypes.AUDIO_FILE) {
        return i18n.translate('settingsPage.reminders.listenAudio')
    } else if (signDoc.type === SignDocTypes.CANVA_PRESENTATION) {
        return i18n.translate('settingsPage.reminders.canvaPresentation')
    } else if (signDoc.type === SignDocTypes.EXAM) {
        return i18n.translate('settingsPage.reminders.exam')
    } else if (signDoc.type === SignDocTypes.LEARNING_PATH) {
        return i18n.translate('settingsPage.reminders.learningPath')
    } else if (signDoc.type === SignDocTypes.LINK) {
        return i18n.translate('settingsPage.reminders.link')
    } else if (signDoc.type === SignDocTypes.WEEZBEE_COURSE) {
        return i18n.translate('settingsPage.reminders.course')
    } else if (signDoc.type === SignDocTypes.SURVEY) {
        return i18n.translate('settingsPage.reminders.survey')
    } else if (signDoc.type === SignDocTypes.FRONTAL_COURSE) {
        return i18n.translate('settingsPage.reminders.frontalCourse')
    } else if (signDoc.type === SignDocTypes.ONLINE_COURSE) {
        return i18n.translate('settingsPage.reminders.onlineCourse')
    } else if (signDoc.type === SignDocTypes.UPLOAD_DOCUMENT) {
        return i18n.translate('settingsPage.reminders.uploadDocument')
    }

}

export const signDocProgressTypes = [SignDocTypes.REGULAR_COURSE, SignDocTypes.EXAM, SignDocTypes.LEARNING_PATH, SignDocTypes.WEEZBEE_COURSE, SignDocTypes.WATCH_AND_SIGN_VIDEO_FILE, SignDocTypes.WATCH_AND_SIGN_VIMEO_LINK, SignDocTypes.WATCH_AND_SIGN_YOUTUBE_LINK, SignDocTypes.AUDIO_FILE, SignDocTypes.SURVEY, SignDocTypes.LINK, SignDocTypes.UPLOAD_DOCUMENT, SignDocTypes.FRONTAL_COURSE, SignDocTypes.ONLINE_COURSE]

/**
 * Determines if a SignDoc is truly complete based on its type and completion criteria.
 * For video SignDocs (WATCH_AND_SIGN_*), requires both progressPercent >= 100 AND signed = true.
 * For other SignDocs, uses the original logic.
 *
 * This function supports two parameter patterns:
 * 1. Individual parameters: {type, progressPercent, viewed, signed}
 * 2. SignDoc object: {signDoc} where signDoc contains the necessary properties
 *
 * @param {object} params - The function parameters container object.
 * @param {string} [params.type] - The SignDoc type (when using individual parameters).
 * @param {number} [params.progressPercent] - The progress percentage (0-100) (when using individual parameters).
 * @param {boolean} [params.viewed] - Whether the SignDoc has been viewed (when using individual parameters).
 * @param {boolean} [params.signed] - Whether the SignDoc has been signed (when using individual parameters).
 * @param {object} [params.signDoc] - The SignDoc object with progress and completion data (when using object parameter).
 * @returns {boolean} True if the SignDoc is complete, false otherwise.
 */
export const isSignDocComplete = ({type, progressPercent, viewed, signed, signDoc}) => {
    let signDocType, signDocProgressPercent, signDocViewed, signDocSigned;

    signDocType = get(signDoc, 'signDoc.type', type) || get(signDoc, 'type', type);
    signDocProgressPercent = get(signDoc, 'progressPercent', progressPercent);
    signDocViewed = get(signDoc, 'viewed', viewed);
    signDocSigned = get(signDoc, 'signed', signed);

    if (signDocProgressTypes.includes(signDocType)) {
        // For other progress-based SignDocs, use original logic
        return signDocProgressPercent === 100;
    } else {
        // For non-progress SignDocs, use original logic
        // can't be signed without being viewed
        return signDocSigned;
    }
};

// Enum for SignDoc types that support duplication functionality
export const DUPLICATABLE_SIGNDOC_TYPES = [
    SignDocTypes.EXAM,
    SignDocTypes.SURVEY
]

export const SignDocsAssingOrganizationInUpdate = {
    [SignDocTypes.EXAM]: true,
    [SignDocTypes.SURVEY]: true,
    [SignDocTypes.LEARNING_PATH]: true,
    [SignDocTypes.WEEZBEE_COURSE]: true,
}

export const SignDocTypesThatSupportApprovals = [
    SignDocTypes.UPLOAD_DOCUMENT,
    SignDocTypes.FRONTAL_COURSE,
    SignDocTypes.ONLINE_COURSE,
]

// Upload document progress constants
export const UPLOAD_PROGRESS_AWAITING_APPROVAL = 50; // Uploaded but awaiting admin approval
export const UPLOAD_PROGRESS_APPROVED = 100; // Approved by admin
export const UPLOAD_PROGRESS_REJECTED = 0; // Rejected by admin

/**
 * Recognized online meeting platforms for course validation.
 * Each platform has a name and list of domain patterns to match against.
 * 
 * @constant {Array<{name: string, patterns: Array<string>}>}
 */
export const MEETING_PLATFORMS = [
    { name: 'Zoom', patterns: ['zoom.us', 'zoom.com', 'zoomgov.com'] },
    { name: 'Microsoft Teams', patterns: ['teams.microsoft.com', 'teams.live.com'] },
    { name: 'Google Meet', patterns: ['meet.google.com', 'hangouts.google.com'] },
    { name: 'Webex', patterns: ['webex.com', 'ciscospark.com'] },
    { name: 'GoToMeeting', patterns: ['gotomeeting.com', 'gotomeet.me', 'join.me'] },
    { name: 'Jitsi', patterns: ['meet.jit.si', 'jitsi.org'] },
    { name: 'Whereby', patterns: ['whereby.com'] },
    { name: 'BlueJeans', patterns: ['bluejeans.com'] },
    { name: 'Skype', patterns: ['join.skype.com', 'skype.com'] },
    { name: 'Discord', patterns: ['discord.gg', 'discord.com'] },
    { name: 'Slack', patterns: ['slack.com'] },
    { name: 'ClickMeeting', patterns: ['clickmeeting.com'] },
    { name: 'BigBlueButton', patterns: ['bigbluebutton.org', 'bbb.'] },
    { name: 'Cisco Webex', patterns: ['webex.com'] },
    { name: 'RingCentral', patterns: ['ringcentral.com', 'meetings.ringcentral.com'] },
    { name: 'Zoho Meeting', patterns: ['zoho.com', 'meeting.zoho.com'] }
];

export const OutcomeStatus = {
    REJECTED: 'REJECTED',   // Admin rejected submission
    NO_SHOW: 'NO_SHOW',     // User didn't attend course session
    FAILED: 'FAILED'        // User failed exam/assessment
};

/**
 * Validates if a URL hostname matches a recognized meeting platform pattern.
 * 
 * Examples:
 * - matchesPlatformPattern('meet.google.com', 'meet.google.com') → true (exact match)
 * - matchesPlatformPattern('sub.meet.google.com', 'meet.google.com') → true (subdomain)
 * - matchesPlatformPattern('fakemeet.google.com', 'meet.google.com') → false (not a match)
 * 
 * @param {string} hostname - The hostname to validate (e.g., 'meet.google.com')
 * @param {string} pattern - The pattern to match against (e.g., 'meet.google.com')
 * @returns {boolean} True if hostname matches the pattern
 */
export const matchesPlatformPattern = (hostname, pattern) => {
    // Exact match or subdomain (ends with .pattern to avoid false positives like 'fakemeet.google.com')
    return hostname === pattern || hostname.endsWith(`.${pattern}`);
};

/**
 * Gets available type options for filtering, with feature flag filtering and video type consolidation.
 * This helper function centralizes the logic for:
 * 1. Filtering types based on company feature flags
 * 2. Consolidating 3 video types into a single "VIDEOS" option for better UX
 * 3. Mapping types to translated labels
 *
 * Used by:
 * - SignDocReports.jsx (unified reports view)
 * - SignDocsListWithFilters.jsx (learning management)
 *
 * @param {object} params - Function parameters
 * @param {Array<string>} params.types - Array of SignDoc types to process (required)
 * @param {Array<string>} [params.filterableTypes=[]] - Optional subset of types to use (if empty, uses all types)
 * @param {object} params.featureFlags - Company feature flags object (required)
 * @param {object} params.i18n - Internationalization object with translate() method (required)
 * @returns {Array<{value: string, text: string}>} Array of type options with value and translated text
 *
 * @example
 * const typeOptions = getAvailableTypeOptions({
 *     types: [SignDocTypes.EXAM, SignDocTypes.SURVEY, SignDocTypes.WATCH_AND_SIGN_VIDEO_FILE],
 *     filterableTypes: [], // Empty = use all types
 *     featureFlags: companyContext.featureFlags,
 *     i18n: i18n
 * });
 * // Returns: [{value: 'EXAM', text: 'Exam'}, {value: 'SURVEY', text: 'Survey'}, {value: 'VIDEOS', text: 'Videos'}]
 */
export const getAvailableTypeOptions = ({types, filterableTypes = [], featureFlags, i18n}) => {
    // Use filterableTypes if provided, otherwise use all types
    const typesToUse = filterableTypes.length > 0 ? filterableTypes : types;

    // Filter types based on feature flags
    const featureFlagFilteredTypes = filter(typesToUse, type => {
        const featureFlagKey = signDocTypeToFeatureFlagMap[type];
        // If no feature flag exists for this type, or if the feature flag is enabled, include it
        return !featureFlagKey || featureFlags[featureFlagKey] === true;
    });

    // Check if any video type exists in the filtered types
    const hasAnyVideoType = some(featureFlagFilteredTypes, type => VIDEO_TYPES.includes(type));

    // Filter out individual video types (they will be consolidated into VIDEOS option)
    const filteredTypes = filter(featureFlagFilteredTypes, type => !VIDEO_TYPES.includes(type));

    // Add the consolidated VIDEOS option if any video type exists
    if (hasAnyVideoType) {
        filteredTypes.push(VIDEO_TYPE);
    }

    // Map types to {value, text} format with translated labels
    return map(filteredTypes, type => ({
        value: type,
        text: i18n.translate(`LearningManagementPage.typeNames.${type}`)
    }));
};
